import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  Dialog,
  Divider,
  makeStyles,
  Paper,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import CommonHeader from 'src/app/components/Header';
import { DEFAULT_CENTER } from 'src/app/views/CMSCameras/helpers';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EMPTY_AGENCY_LABEL = 'Chưa có đại lý';

const provinceCoordinateMap = {
  'Hà Nội': { lat: 21.028511, lng: 105.804817 },
  'Cao Bằng': { lat: 22.666641, lng: 106.263893 },
  'Tuyên Quang': { lat: 21.823221, lng: 105.21817 },
  'Lào Cai': { lat: 22.480943, lng: 103.975495 },
  'Điện Biên': { lat: 21.386023, lng: 103.02301 },
  'Lai Châu': { lat: 22.396428, lng: 103.458244 },
  'Sơn La': { lat: 21.328463, lng: 103.914768 },
  'Thái Nguyên': { lat: 21.594339, lng: 105.848167 },
  'Lạng Sơn': { lat: 21.853708, lng: 106.76152 },
  'Quảng Ninh': { lat: 21.006382, lng: 107.292511 },
  'Phú Thọ': { lat: 21.322738, lng: 105.40198 },
  'Bắc Ninh': { lat: 21.121444, lng: 106.111053 },
  'Hải Phòng': { lat: 20.844912, lng: 106.688084 },
  'Hưng Yên': { lat: 20.852571, lng: 106.016998 },
  'Ninh Bình': { lat: 20.250615, lng: 105.974453 },
  'Thanh Hóa': { lat: 19.806692, lng: 105.785179 },
  'Nghệ An': { lat: 18.679585, lng: 105.681335 },
  'Hà Tĩnh': { lat: 18.355953, lng: 105.887749 },
  'Quảng Trị': { lat: 16.740307, lng: 107.185467 },
  'Thừa Thiên Huế': { lat: 16.463713, lng: 107.590866 },
  'Đà Nẵng': { lat: 16.054407, lng: 108.202164 },
  'Quảng Ngãi': { lat: 15.12047, lng: 108.79232 },
  'Khánh Hòa': { lat: 12.238791, lng: 109.196749 },
  'Gia Lai': { lat: 13.98333, lng: 108.0 },
  'Đắk Lắk': { lat: 12.666667, lng: 108.05 },
  'Lâm Đồng': { lat: 11.940419, lng: 108.458313 },
  'Tây Ninh': { lat: 11.367541, lng: 106.126171 },
  'Đồng Nai': { lat: 10.957412, lng: 106.842613 },
  'Hồ Chí Minh': { lat: 10.776889, lng: 106.700806 },
  'Vĩnh Long': { lat: 10.239574, lng: 105.957191 },
  'Đồng Tháp': { lat: 10.493799, lng: 105.688179 },
  'An Giang': { lat: 10.521584, lng: 105.125893 },
  'Cần Thơ': { lat: 10.045162, lng: 105.746857 },
  'Cà Mau': { lat: 9.17694, lng: 105.15242 }
};

const formatDeviceCount = value => (Number(value) || 0).toLocaleString('en-US');

const createProvinceIcon = (provinceName, deviceCount) =>
  L.divIcon({
    className: 'agency-map-marker',
    html: `<div style="display:flex;flex-direction:column;align-items:center;">
      <div style="margin-bottom:4px;padding:2px 6px;max-width:96px;background:rgba(255,255,255,0.94);color:#0F172A;border:1px solid rgba(203,213,225,0.95);border-radius:999px;font-size:11px;font-weight:600;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;box-shadow:0 6px 14px rgba(15,23,42,0.14);">${provinceName}</div>
      <div style="background:#D9042F;color:#fff;border:2px solid rgba(255,255,255,0.92);border-radius:999px;padding:5px 9px;min-width:42px;text-align:center;font-size:10px;font-weight:700;line-height:1;box-shadow:0 10px 20px rgba(15,23,42,0.22);">${deviceCount.toLocaleString('en-US')}</div>
    </div>`,
    iconAnchor: [21, 30],
    tooltipAnchor: [0, -30]
  });

function AgencyMapViewport({ provinces }) {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (provinces.length === 0) return;

    const timer = window.setTimeout(() => {
      if (provinces.length === 1) {
        map.setView([provinces[0].lat, provinces[0].lng], 10, { animate: true });
        return;
      }

      const bounds = L.latLngBounds(provinces.map(province => [province.lat, province.lng]));
      map.fitBounds(bounds, { padding: [48, 48], animate: true });
    }, 180);

    return () => window.clearTimeout(timer);
  }, [map, provinces]);

  return null;
}

AgencyMapViewport.propTypes = {
  provinces: PropTypes.array.isRequired
};

function AgencyDetailModal({ open, onClose, listProvinces, listAgenciesByProvince }) {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const provincesPerPage = 15;

  const provinceSeedData = useMemo(
    () =>
      (listProvinces || []).map(province => {
        const coordinates = provinceCoordinateMap[province.name] || DEFAULT_CENTER;
        const agencies = (listAgenciesByProvince || [])
          .filter(agency => String(agency?.leader?.citizen_id) === String(province.id))
          .map(agency => ({
            agency: agency.name,
            deviceCount: formatDeviceCount(agency.devicesTotal)
          }));

        return {
          province: province.name,
          lat: coordinates.lat,
          lng: coordinates.lng,
          agencies:
            agencies.length > 0
              ? agencies
              : [{ agency: EMPTY_AGENCY_LABEL, deviceCount: '0' }]
        };
      }),
    [listAgenciesByProvince, listProvinces]
  );

  const groupedDetailRows = useMemo(
    () =>
      provinceSeedData.map(province => ({
        province: province.province,
        totalDeviceCount: province.agencies.reduce(
          (total, agency) =>
            total + (Number(String(agency.deviceCount).replace(/,/g, '')) || 0),
          0
        ),
        rows: province.agencies.map(agency => ({
          province: province.province,
          agency: agency.agency,
          deviceCount: agency.deviceCount,
          lat: province.lat,
          lng: province.lng
        }))
      })),
    [provinceSeedData]
  );

  const provinceSummaries = useMemo(
    () =>
      provinceSeedData.map(province => {
        const validAgencies = province.agencies.filter(
          agency => agency.agency !== EMPTY_AGENCY_LABEL
        );
        const deviceCount = validAgencies.reduce(
          (total, agency) => total + (Number(String(agency.deviceCount).replace(/,/g, '')) || 0),
          0
        );

        return {
          province: province.province,
          lat: province.lat,
          lng: province.lng,
          agencyCount: validAgencies.length,
          deviceCount
        };
      }),
    [provinceSeedData]
  );

  const provincesWithDevices = useMemo(
    () => provinceSummaries.filter(province => province.deviceCount > 0),
    [provinceSummaries]
  );

  const totalProvinces = groupedDetailRows.length;
  const totalPages = Math.ceil(totalProvinces / provincesPerPage);
  const startIndex = (page - 1) * provincesPerPage;
  const endIndex = Math.min(startIndex + provincesPerPage, totalProvinces);
  const paginatedGroups = groupedDetailRows.slice(startIndex, endIndex);

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open]);

  const handlePageChange = (_, nextPage) => {
    setPage(nextPage);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.modalLayout}>
        <AppBar className={classes.appBar}>
          <CommonHeader
            title="Chi tiết phân bổ thiết bị theo tỉnh thành"
            closeToolbarRef={onClose}
          />
        </AppBar>
        <Box className={classes.root}>
          <Box className={classes.mapStage}>
            <Box className={classes.mapCanvas}>
              <MapContainer
                center={DEFAULT_CENTER}
                zoom={6}
                minZoom={6}
                dragging
                zoomControl={false}
                style={{ height: '100%', width: '100%' }}
              >
                <AgencyMapViewport provinces={provincesWithDevices} />
                <TileLayer
                  url="https://{s}.google.com/vt/lyrs=m&hl=vi&x={x}&y={y}&z={z}"
                  subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                  maxZoom={20}
                />
                {provincesWithDevices.map(province => (
                  <Marker
                    key={province.province}
                    position={[province.lat, province.lng]}
                    icon={createProvinceIcon(province.province, province.deviceCount)}
                  >
                    <LeafletTooltip
                      direction="top"
                      offset={[-15, 15]}
                      opacity={1}
                      className="agencyTooltip"
                    >
                      <Box className={classes.markerTooltip}>
                        <Typography className={classes.markerTitle}>
                          {province.province}
                        </Typography>
                        <Typography className={classes.markerText}>
                          {province.agencyCount} đại lý
                        </Typography>
                        <Typography className={classes.markerText}>
                          {province.deviceCount.toLocaleString('en-US')} thiết bị
                        </Typography>
                      </Box>
                    </LeafletTooltip>
                  </Marker>
                ))}
              </MapContainer>
            </Box>
            <Paper elevation={6} className={classes.dataCard}>
              <Typography className={classes.cardTitle}>
                Danh sách thiết bị theo tỉnh thành
              </Typography>
              <Typography className={classes.cardDescription}>
                Báo cáo chi tiết số lượng thiết bị phân bố theo đại lý và khu vực.
              </Typography>
              <Divider className={classes.divider} />
              <Box className={classes.tableContainer}>
                <Table size="small" className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.headCell} ${classes.sttCell}`}>
                        STT
                      </TableCell>
                      <TableCell className={`${classes.headCell} ${classes.provinceCell}`}>
                        Tỉnh thành
                      </TableCell>
                      <TableCell className={`${classes.headCell} ${classes.agencyCell}`}>
                        Đại lý
                      </TableCell>
                      <TableCell
                        align="right"
                        className={`${classes.headCell} ${classes.deviceCountCell}`}
                      >
                        Số thiết bị
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedGroups.map((group, groupIndex) =>
                      group.rows.map((row, rowIndex) => (
                        <TableRow key={`${row.province}-${row.agency}-${rowIndex}`}>
                          {rowIndex === 0 && (
                          <TableCell
                            rowSpan={group.rows.length}
                            className={`${classes.bodyCell} ${classes.sttCell}`}
                          >
                            {startIndex + groupIndex + 1}
                          </TableCell>
                        )}
                        {rowIndex === 0 && (
                          <TableCell
                            rowSpan={group.rows.length}
                            className={`${classes.bodyCell} ${classes.provinceCell}`}
                          >
                            <Box className={classes.provinceNameBlock}>
                              <Typography component="div" className={classes.provinceName}>
                                {group.province}
                              </Typography>
                              <Typography component="div" className={classes.provinceDeviceTotal}>
                                ({group.totalDeviceCount.toLocaleString('en-US')})
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell className={`${classes.bodyCell} ${classes.agencyCell}`}>
                          {row.agency}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={`${classes.bodyCell} ${classes.deviceCountCell}`}
                        >
                          {row.deviceCount}
                        </TableCell>
                      </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
              <Box className={classes.footer}>
                <Typography className={classes.footerText}>
                  Hiển thị {startIndex + 1} - {endIndex} trong tổng số {totalProvinces}{' '}
                  tỉnh/thành
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  size="small"
                  variant="outlined"
                  shape="rounded"
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  appBar: {
    position: 'relative',
    boxShadow: 'none !important'
  },
  dialogPaper: {
    width: 'calc(100vw - 48px)',
    maxWidth: 1480,
    height: 'calc(100vh - 48px)',
    maxHeight: 'calc(100vh - 48px)',
    margin: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 16px)',
      height: 'calc(100vh - 16px)',
      maxHeight: 'calc(100vh - 16px)',
      margin: 8
    }
  },
  modalLayout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    background: '#F4F7FB'
  },
  mapStage: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 0,
    padding: 0,
    background: '#F4F7FB',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      overflow: 'auto'
    }
  },
  mapCanvas: {
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 0,
    background: '#9EE6F6',
    '& .leaflet-container': {
      width: '100%',
      height: '100%'
    },
    '& .leaflet-tooltip.agencyTooltip': {
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      color: '#111827',
      padding: 0
    },
    '& .leaflet-tooltip.agencyTooltip:before': {
      borderTopColor: '#FFFFFF'
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 320
    }
  },
  dataCard: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    borderRadius: 0,
    padding: theme.spacing(0, 2.5, 2),
    boxShadow: 'none',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2)
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 420
    }
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1F2937'
  },
  cardDescription: {
    marginTop: theme.spacing(0.75),
    fontSize: 13,
    lineHeight: 1.5,
    color: '#6B7280'
  },
  divider: {
    margin: theme.spacing(1.5, 0, 1)
  },
  table: {
    tableLayout: 'fixed'
  },
  tableContainer: {
    flex: 1,
    minHeight: 0,
    maxHeight: 800,
    overflowY: 'auto',
    overflowX: 'hidden',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#CBD5E1',
      borderRadius: 999
    }
  },
  headCell: {
    fontSize: 12,
    fontWeight: 700,
    color: '#475569',
    background: '#F8FAFC',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    verticalAlign: 'middle'
  },
  bodyCell: {
    fontSize: 13,
    color: '#334155',
    borderBottom: '1px solid #E5E7EB',
    verticalAlign: 'middle'
  },
  sttCell: {
    width: 44
  },
  provinceCell: {
    width: 130,
    textAlign: 'center'
  },
  provinceNameBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  },
  provinceName: {
    fontSize: 13,
    color: '#334155',
    lineHeight: 1.4
  },
  provinceDeviceTotal: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 1.3
  },
  agencyCell: {
    width: 'auto'
  },
  deviceCountCell: {
    width: 92
  },
  footer: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  },
  footerText: {
    fontSize: 12,
    color: '#64748B'
  },
  markerTooltip: {
    padding: theme.spacing(0.75, 1),
    background: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 12,
    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.16)'
  },
  markerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#111827'
  },
  markerText: {
    fontSize: 12,
    color: '#4B5563'
  }
}));

AgencyDetailModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  listProvinces: PropTypes.array,
  listAgenciesByProvince: PropTypes.array
};

export default AgencyDetailModal;
